import type { Feature, FeatureCollection, MultiPolygon, Point, Polygon, Position } from 'geojson';

import type { FeatureProperties, VectorFeatureCollection } from '../data/model';

type Ring = readonly Position[];
type PolygonCoordinates = readonly Ring[];
type FinitePosition = [number, number, ...number[]];

interface Cell {
  readonly x: number;
  readonly y: number;
  readonly halfSize: number;
  readonly distance: number;
  readonly maximumDistance: number;
}

const SQRT_TWO = Math.SQRT2;
const MAX_SEARCH_CELLS = 50_000;

function isFinitePosition(position: Position | undefined): position is FinitePosition {
  return (
    Array.isArray(position) &&
    position.length >= 2 &&
    typeof position[0] === 'number' &&
    Number.isFinite(position[0]) &&
    typeof position[1] === 'number' &&
    Number.isFinite(position[1])
  );
}

function isUsableRing(ring: Ring | undefined): ring is Ring {
  return Boolean(ring && ring.length >= 4 && ring.every(isFinitePosition));
}

function ringArea(ring: Ring): number {
  let twiceArea = 0;
  for (let index = 0; index < ring.length; index += 1) {
    const current = ring[index];
    const next = ring[(index + 1) % ring.length];
    if (!isFinitePosition(current) || !isFinitePosition(next)) return 0;
    twiceArea += current[0] * next[1] - next[0] * current[1];
  }
  return twiceArea / 2;
}

function polygonArea(coordinates: PolygonCoordinates): number {
  const exterior = coordinates[0];
  if (!isUsableRing(exterior)) return 0;
  const exteriorArea = Math.abs(ringArea(exterior));
  const holesArea = coordinates
    .slice(1)
    .reduce((total, ring) => total + (isUsableRing(ring) ? Math.abs(ringArea(ring)) : 0), 0);
  return Math.max(0, exteriorArea - holesArea);
}

function pointInRing(x: number, y: number, ring: Ring): boolean {
  let inside = false;
  for (let currentIndex = 0, previousIndex = ring.length - 1; currentIndex < ring.length;) {
    const current = ring[currentIndex];
    const previous = ring[previousIndex];
    if (!isFinitePosition(current) || !isFinitePosition(previous)) return false;
    const intersects =
      current[1] > y !== previous[1] > y &&
      x < ((previous[0] - current[0]) * (y - current[1])) / (previous[1] - current[1]) + current[0];
    if (intersects) inside = !inside;
    previousIndex = currentIndex;
    currentIndex += 1;
  }
  return inside;
}

function pointInPolygon(x: number, y: number, coordinates: PolygonCoordinates): boolean {
  const exterior = coordinates[0];
  if (!isUsableRing(exterior) || !pointInRing(x, y, exterior)) return false;
  return !coordinates.slice(1).some((hole) => isUsableRing(hole) && pointInRing(x, y, hole));
}

function squaredSegmentDistance(
  x: number,
  y: number,
  start: FinitePosition,
  end: FinitePosition,
): number {
  let segmentX = end[0] - start[0];
  let segmentY = end[1] - start[1];
  let pointX = x - start[0];
  let pointY = y - start[1];
  const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;
  if (segmentLengthSquared > 0) {
    const projection = (pointX * segmentX + pointY * segmentY) / segmentLengthSquared;
    if (projection > 1) {
      pointX = x - end[0];
      pointY = y - end[1];
    } else if (projection > 0) {
      pointX -= segmentX * projection;
      pointY -= segmentY * projection;
    }
  }
  segmentX = pointX;
  segmentY = pointY;
  return segmentX * segmentX + segmentY * segmentY;
}

function signedDistanceToPolygon(x: number, y: number, coordinates: PolygonCoordinates): number {
  let minimumSquaredDistance = Number.POSITIVE_INFINITY;
  for (const ring of coordinates) {
    if (!isUsableRing(ring)) continue;
    for (let index = 0; index < ring.length; index += 1) {
      const start = ring[index];
      const end = ring[(index + 1) % ring.length];
      if (!isFinitePosition(start) || !isFinitePosition(end)) return Number.NEGATIVE_INFINITY;
      minimumSquaredDistance = Math.min(
        minimumSquaredDistance,
        squaredSegmentDistance(x, y, start, end),
      );
    }
  }
  if (!Number.isFinite(minimumSquaredDistance)) return Number.NEGATIVE_INFINITY;
  const distance = Math.sqrt(minimumSquaredDistance);
  return pointInPolygon(x, y, coordinates) ? distance : -distance;
}

function createCell(x: number, y: number, halfSize: number, coordinates: PolygonCoordinates): Cell {
  const distance = signedDistanceToPolygon(x, y, coordinates);
  return {
    x,
    y,
    halfSize,
    distance,
    maximumDistance: distance + halfSize * SQRT_TWO,
  };
}

class CellQueue {
  readonly #cells: Cell[] = [];

  get size(): number {
    return this.#cells.length;
  }

  push(cell: Cell): void {
    const cells = this.#cells;
    cells.push(cell);
    let index = cells.length - 1;
    while (index > 0) {
      const parent = Math.floor((index - 1) / 2);
      const parentCell = cells[parent];
      if (!parentCell || parentCell.maximumDistance >= cell.maximumDistance) break;
      cells[index] = parentCell;
      index = parent;
    }
    cells[index] = cell;
  }

  pop(): Cell | undefined {
    const cells = this.#cells;
    const first = cells[0];
    const last = cells.pop();
    if (!first || !last || cells.length === 0) return first;
    let index = 0;
    while (true) {
      const leftIndex = index * 2 + 1;
      const rightIndex = leftIndex + 1;
      const left = cells[leftIndex];
      const right = cells[rightIndex];
      if (!left) break;
      const childIndex =
        right && right.maximumDistance > left.maximumDistance ? rightIndex : leftIndex;
      const child = cells[childIndex];
      if (!child || child.maximumDistance <= last.maximumDistance) break;
      cells[index] = child;
      index = childIndex;
    }
    cells[index] = last;
    return first;
  }
}

function centroidCell(coordinates: PolygonCoordinates): Cell | null {
  const ring = coordinates[0];
  if (!isUsableRing(ring)) return null;
  let weightedX = 0;
  let weightedY = 0;
  let weight = 0;
  for (let index = 0; index < ring.length; index += 1) {
    const current = ring[index];
    const next = ring[(index + 1) % ring.length];
    if (!isFinitePosition(current) || !isFinitePosition(next)) return null;
    const cross = current[0] * next[1] - next[0] * current[1];
    weightedX += (current[0] + next[0]) * cross;
    weightedY += (current[1] + next[1]) * cross;
    weight += cross;
  }
  if (weight === 0) return null;
  return createCell(weightedX / (3 * weight), weightedY / (3 * weight), 0, coordinates);
}

/** Finds a deterministic interior point with generous clearance from polygon edges and holes. */
export function representativePointForPolygon(
  coordinates: PolygonCoordinates,
): readonly [number, number] | null {
  const exterior = coordinates[0];
  if (!isUsableRing(exterior) || polygonArea(coordinates) <= 0) return null;

  let minimumX = Number.POSITIVE_INFINITY;
  let minimumY = Number.POSITIVE_INFINITY;
  let maximumX = Number.NEGATIVE_INFINITY;
  let maximumY = Number.NEGATIVE_INFINITY;
  for (const position of exterior) {
    if (!isFinitePosition(position)) return null;
    minimumX = Math.min(minimumX, position[0]);
    minimumY = Math.min(minimumY, position[1]);
    maximumX = Math.max(maximumX, position[0]);
    maximumY = Math.max(maximumY, position[1]);
  }
  const width = maximumX - minimumX;
  const height = maximumY - minimumY;
  if (!(width > 0) || !(height > 0)) return null;

  const halfSize = Math.max(width, height) / 2;
  const queue = new CellQueue();
  queue.push(createCell(minimumX + width / 2, minimumY + height / 2, halfSize, coordinates));
  let best = centroidCell(coordinates);
  const centre = createCell(minimumX + width / 2, minimumY + height / 2, 0, coordinates);
  if (!best || centre.distance > best.distance) best = centre;
  const precision = Math.max(
    Math.min(Math.max(width, height) / 1024, Math.min(width, height) / 64),
    1e-9,
  );

  let visited = 0;
  while (queue.size > 0 && visited < MAX_SEARCH_CELLS) {
    const cell = queue.pop();
    if (!cell) break;
    visited += 1;
    if (!best || cell.distance > best.distance) best = cell;
    if (cell.maximumDistance - (best?.distance ?? 0) <= precision || cell.halfSize <= precision) {
      continue;
    }
    const nextHalfSize = cell.halfSize / 2;
    queue.push(createCell(cell.x - nextHalfSize, cell.y - nextHalfSize, nextHalfSize, coordinates));
    queue.push(createCell(cell.x + nextHalfSize, cell.y - nextHalfSize, nextHalfSize, coordinates));
    queue.push(createCell(cell.x - nextHalfSize, cell.y + nextHalfSize, nextHalfSize, coordinates));
    queue.push(createCell(cell.x + nextHalfSize, cell.y + nextHalfSize, nextHalfSize, coordinates));
  }

  return best && best.distance > 0 && pointInPolygon(best.x, best.y, coordinates)
    ? [best.x, best.y]
    : null;
}

function largestPolygonPart(geometry: Polygon | MultiPolygon): PolygonCoordinates | null {
  const parts: readonly PolygonCoordinates[] =
    geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
  let largest: PolygonCoordinates | null = null;
  let largestArea = 0;
  for (const part of parts) {
    const area = polygonArea(part);
    if (area > largestArea) {
      largest = part;
      largestArea = area;
    }
  }
  return largest;
}

const polygonLabelAnchorCache = new WeakMap<
  VectorFeatureCollection,
  FeatureCollection<Point, FeatureProperties>
>();

/**
 * Derives one transient point feature for every valid original polygon feature.
 * The cache is keyed only by authoritative source-data identity, never label style.
 */
export function polygonLabelAnchors(
  data: VectorFeatureCollection,
): FeatureCollection<Point, FeatureProperties> {
  const cached = polygonLabelAnchorCache.get(data);
  if (cached) return cached;
  const features: Feature<Point, FeatureProperties>[] = [];
  for (const feature of data.features) {
    const geometry = feature.geometry;
    if (geometry?.type !== 'Polygon' && geometry?.type !== 'MultiPolygon') continue;
    const part = largestPolygonPart(geometry);
    const coordinates = part ? representativePointForPolygon(part) : null;
    if (!coordinates) continue;
    features.push({
      type: 'Feature',
      ...(feature.id === undefined ? {} : { id: feature.id }),
      properties: feature.properties,
      geometry: { type: 'Point', coordinates: [...coordinates] },
    });
  }
  const anchors = { type: 'FeatureCollection' as const, features };
  polygonLabelAnchorCache.set(data, anchors);
  return anchors;
}
